import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../components/AuthContext';
import { LogoFull } from '../components/Logo';

function BetaModal({ onClose }) {
  const [dontShow, setDontShow] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/beta-notice.txt')
      .then(r => r.text())
      .then(t => { setText(t); setLoading(false); })
      .catch(() => { setText('Welcome to the TxtMyCar beta. Thank you for testing!'); setLoading(false); });
  }, []);

  const handleClose = () => {
    if (dontShow) {
      localStorage.setItem('txtmycar_beta_dismissed', '1');
    }
    onClose();
  };

  const paragraphs = text.split('\n').filter(p => p.trim().length > 0);

  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, backdropFilter: 'blur(4px)',
      }}>
        {/* Modal */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 20, padding: 36, width: '100%', maxWidth: 480,
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          borderTop: '3px solid var(--accent)',
          animation: 'modalIn 0.25s cubic-bezier(0.16,1,0.3,1)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{
              background: 'var(--accent)', color: '#0f0f13',
              borderRadius: 8, padding: '4px 12px',
              fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 12,
              letterSpacing: 1, textTransform: 'uppercase',
            }}>
              Beta
            </div>
            <h2 style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 800,
              fontSize: 20, color: 'var(--text)', letterSpacing: -0.5,
            }}>
              Welcome to TxtMyCar Beta
            </h2>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--border)', marginBottom: 20 }} />

          {/* Content */}
          <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 24, paddingRight: 4 }}>
            {loading ? (
              <p style={{ color: 'var(--muted)', fontSize: 14 }}>Loading...</p>
            ) : (
              paragraphs.map((para, i) => (
                <p key={i} style={{
                  color: 'var(--muted)', fontSize: 14, lineHeight: 1.75,
                  marginBottom: i < paragraphs.length - 1 ? 14 : 0,
                }}>
                  {para}
                </p>
              ))
            )}
          </div>

          {/* Don't show again */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', background: 'var(--surface2)',
            borderRadius: 10, marginBottom: 20, cursor: 'pointer',
          }} onClick={() => setDontShow(v => !v)}>
            <div style={{
              width: 20, height: 20, borderRadius: 5, flexShrink: 0,
              border: `2px solid ${dontShow ? 'var(--accent)' : 'var(--border)'}`,
              background: dontShow ? 'var(--accent)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}>
              {dontShow && <span style={{ color: '#0f0f13', fontSize: 13, fontWeight: 900, lineHeight: 1 }}>&#10003;</span>}
            </div>
            <span style={{ fontSize: 13, color: 'var(--muted)', userSelect: 'none' }}>
              Don't show this again
            </span>
          </div>

          {/* Close button */}
          <button onClick={handleClose} style={{
            width: '100%', background: 'var(--accent)', border: 'none',
            borderRadius: 10, color: '#0f0f13', cursor: 'pointer',
            fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 15,
            padding: '13px', transition: 'opacity 0.15s',
          }}>
            Got it, let's go
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBeta, setShowBeta] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('txtmycar_beta_dismissed');
    if (!dismissed) setShowBeta(true);
  }, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setLoading(true);
    try {
      const data = await api.auth.login(form);
      login(data.token, data.user);
      navigate('/dashboard');
    } catch (err) {
      if (err.message && err.message.includes('verify your email')) {
        setUnverified(true);
        setResendEmail(form.email);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await api.auth.resendVerification(resendEmail);
      setResendSent(true);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      {showBeta && <BetaModal onClose={() => setShowBeta(false)} />}

      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <LogoFull iconSize={64} />
          </div>

          <h1 className="auth-title">Welcome back</h1>
          <p className="auth-subtitle">Sign in to your account</p>

          {error && <div className="alert alert-error">{error}</div>}

          {unverified && (
            <div className="alert alert-error">
              <p style={{ marginBottom: 8 }}>Please verify your email address before logging in.</p>
              {resendSent ? (
                <p style={{ color: 'var(--success)', fontSize: 13 }}>Verification email resent — check your inbox.</p>
              ) : (
                <button onClick={handleResend}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                  Resend verification email
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email address</label>
              <input type="email" name="email" value={form.email}
                onChange={handleChange} placeholder="you@example.com" required autoComplete="email" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" name="password" value={form.password}
                onChange={handleChange} placeholder="••••••••" required autoComplete="current-password" />
            </div>
            <div style={{ textAlign: 'right', marginTop: -10, marginBottom: 16 }}><Link to="/forgot-password" style={{ fontSize: 13, color: '#7a7a94', textDecoration: 'none' }}>Forgot password?</Link></div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="spinner"></span> Signing in...</> : 'Sign in'}
            </button>
          </form>

          <p className="auth-link-text">
            Don't have an account? <Link to="/signup">Create one</Link>
          </p>
        </div>
      </div>
    </>
  );
}
